/**
 * As linhas da tabela de vencimentos (#363): uma por data que o tipo usa (`dues.*` é `null`
 * quando não usa). Dias e motivos vêm do servidor; aqui só viram texto e chip.
 */
import type { FireDueField, FireEquipment, FireEquipmentLabels } from '../../../types/fireEquipment';
import { dueState } from '../dueState';
import { formatIsoDate } from '../fireDates';

export interface DueRow {
  field: FireDueField;
  title: string;
  hint: string;
  date: string;
  chip: { situation: 'VENCIDO' | 'A_VENCER' | 'SEM_INSPECAO' | 'EM_DIA' | 'FORA_DE_USO'; days?: number; label?: string };
}

const FIELD_ORDER: readonly FireDueField[] = ['charge', 'hydrostatic', 'nextInspection'];

const pluralDays = (count: number): string => `${count} ${count === 1 ? 'dia' : 'dias'}`;

function titleOf(field: FireDueField): string {
  if (field === 'charge') return 'Validade da carga';
  if (field === 'hydrostatic') return 'Validade do teste hidrostático';
  return 'Próxima inspeção';
}

function hintOf(field: FireDueField, equipment: FireEquipment, labels: Pick<FireEquipmentLabels, 'frequency'>): string {
  if (field === 'charge') return 'Quando o extintor precisa ser recarregado';
  if (field === 'hydrostatic') return equipment.type === 'HYDRANT' ? 'Da mangueira do hidrante' : 'Do cilindro';

  const frequency = labels.frequency[equipment.inspectionFrequency];
  return equipment.lastInspectionDate
    ? `${frequency}, a partir da última em ${formatIsoDate(equipment.lastInspectionDate)}`
    : `${frequency}, ainda sem inspeção registrada`;
}

function chipOf(
  field: FireDueField,
  equipment: FireEquipment,
  days: number | null,
  labels: Pick<FireEquipmentLabels, 'situation'>
): DueRow['chip'] {
  // Fora de uso a data guardada pode estar no passado e não significa atraso.
  if (equipment.status === 'DECOMMISSIONED' || days === null) {
    return { situation: 'FORA_DE_USO', label: labels.situation.FORA_DE_USO };
  }
  const state = dueState(equipment, field);
  if (state === 'VENCIDO') return { situation: 'VENCIDO', days };
  if (state === 'A_VENCER') return { situation: 'A_VENCER', days };

  if (state === 'PRIMEIRA_INSPECAO') {
    return {
      situation: 'SEM_INSPECAO',
      label: days === 0 ? '1ª inspeção, hoje' : `1ª inspeção, em ${pluralDays(days)}`,
    };
  }
  return { situation: 'EM_DIA', label: labels.situation.EM_DIA };
}

/** As linhas da tabela, na ordem do desenho: carga, teste hidrostático, próxima inspeção. */
export function dueRows(
  equipment: FireEquipment,
  labels: Pick<FireEquipmentLabels, 'frequency' | 'situation'>
): DueRow[] {
  return FIELD_ORDER.flatMap((field) => {
    const due = equipment.dues[field];
    if (!due) return [];
    return [
      {
        field,
        title: titleOf(field),
        hint: hintOf(field, equipment, labels),
        date: due.date,
        chip: chipOf(field, equipment, due.days, labels),
      },
    ];
  });
}
